import { useCallback } from 'react';
import { useNewDealForm } from './useNewDealForm';
import { useProfitFactor } from './useProfitFactor';
import { useCloseAtLoss } from './useCloseAtLoss';
import { useCloseAtProfit } from './useCloseAtProfit';
import { useOpenAtPrice } from './useOpenAtPrice';

import {
  openOrder,
  OrderTypes,
} from '../../../common/constants/trading.constants';

export const useNewDealFullForm = ({
  account,
  store,
  overrideSymbolProperties,
  calculateMaxVolumeDependingOnOpenAtPrice = false
}) => {
  const {
    notEnoughFreeMargin,
    freeMargin,
    symbolProperties,
    ui,
    tickSize,
    blockedRangeForDelayedOrders,
    params: { limitOfInvestment, absoluteLimitOfInvestment, volume },
  } = useNewDealForm({
    account,
    store,
    calculateMaxVolumeDependingOnOpenAtPrice,
    overrideSymbolProperties,
  });

  const profitFactor = useProfitFactor({
    absoluteLimitOfInvestment,
    store,
  });

  const closeAtLoss = useCloseAtLoss({
    store,
    symbolProperties,
    volume,
    absoluteLimitOfInvestment,
  });

  const closeAtProfit = useCloseAtProfit({
    absoluteLimitOfInvestment,
    store,
    volume,
    symbolProperties,
  });
  const openAtPrice = useOpenAtPrice({
    blockedRangeForDelayedOrders,
    tickSize,
    store,
  });

  const openAtPriceProblems =
    openAtPrice &&
    openAtPrice.value < blockedRangeForDelayedOrders.max &&
    openAtPrice.value > blockedRangeForDelayedOrders.min;

  const placeOrder = useCallback(
    (orderType, override = {}) => {
      const newOrder = { ...openOrder };

      if (orderType === OrderTypes.Buy) {
        newOrder.type = OrderTypes.Buy;
        if (store.openAtPrice) {
          if (store.openAtPriceOf > symbolProperties.price.ask) {
            newOrder.type = OrderTypes.BuyStop;
          } else {
            newOrder.type = OrderTypes.BuyLimit;
          }
          newOrder.price = store.openAtPriceOf;
        } else {
          newOrder.price = symbolProperties.price.ask;
        }
      }
      if (orderType === OrderTypes.Sell) {
        newOrder.type = OrderTypes.Sell;
        if (store.openAtPrice) {
          if (store.openAtPriceOf < symbolProperties.price.bid) {
            newOrder.type = OrderTypes.SellStop;
          } else {
            newOrder.type = OrderTypes.SellLimit;
          }
          newOrder.price = store.openAtPriceOf;
        } else {
          newOrder.price = symbolProperties.price.bid;
        }
      }
      newOrder.symbol = account.symbol.symbol;
      newOrder.volume = volume.value;

      if (store.closeAtLoss) {
        newOrder.max_lost = closeAtLoss.value;
      }

      if (store.closeAtProfit) {
        newOrder.max_profit = store.closeAtProfitOf;
      }

      newOrder.deviation = 100;
      const orderOverride = {
        ...newOrder,
        ...override,
      };

      account.OpenOrder(orderOverride);
    },
    [
      store.closeAtProfitOf,
      store.closeAtProfit,
      store.openAtPrice,
      store.openAtPriceOf,
      store.closeAtLoss,
      account,
      volume.value,
      symbolProperties.price.ask,
      symbolProperties.price.bid,
      closeAtLoss.value,
    ]
  );

  const buy = useCallback(
    (override = {}) => {
      placeOrder(OrderTypes.Buy, override);
    },
    [placeOrder]
  );

  const sell = useCallback(
    (override = {}) => {
      placeOrder(OrderTypes.Sell, override);
    },
    [placeOrder]
  );

  return {
    notEnoughFreeMargin,
    openAtPriceProblems,
    freeMargin,
    symbolProperties,
    ui,

    blockedRangeForDelayedOrders,
    setIsFullFormShown: store.setIsFullFormShown,
    isFullFormShown: store.isFullFormShown,

    actions: {
      buy,
      sell,
    },

    params: {
      limitOfInvestment,
      absoluteLimitOfInvestment,
      profitFactor,
      volume,
      closeAtLoss,
      closeAtProfit,
      openAtPrice,
      sl: {
        value: store.stopLossLevel,
      },
      tp: {
        value: store.takeProfitLevel,
      },
    },
  };
};
